import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { flowsheetUrl } from './api';
import { clientApi } from '@/config/axios';
import { useCareViews } from './careViews';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import type { OfflineFlowsheetController } from './useOfflineFlowsheet';
interface Candidate {
  episode_id: string;
  label: string;
  next_visit: string | null;
  patient_id: string;
}
export function OfflinePreparation({ controller }: { controller: OfflineFlowsheetController }) {
  const { user, organization } = useAuth();
  const org = organization?.organization_id;
  const scope = `${org}:${user?.id}`;
  const [all, setAll] = useState(false);
  const [search, setSearch] = useState('');
  const [pins, setPins] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const views = useCareViews('roster');
  const pinView = views.data?.find((v) => v.name === 'Offline pinned episodes' && v.owner_id === user?.id);
  const query = useQuery({
    enabled: !!org && navigator.onLine,
    queryKey: ['offline-preparation-roster', org, all, search],
    queryFn: async () =>
      (
        await clientApi.get<{ total: number; data: Candidate[] }>(`${flowsheetUrl(org!)}/preparation-roster`, {
          params: { search, limit: 200, include_all: all },
        })
      ).data,
  });
  useEffect(() => {
    if (views.data) setPins(pinView?.expanded || {});
  }, [views.data, pinView]);
  const upcoming = useRef<Candidate[]>([]);
  if (!all && !search && query.data) upcoming.current = query.data.data;
  const auto = useRef('');
  const prepare = async (persist: boolean, selectedPins = pins) => {
    setBusy(true);
    try {
      if (persist)
        await views.save({
          shared: false,
          expanded: selectedPins,
          name: 'Offline pinned episodes',
          expected_version: pinView?.version || 0,
        });
      const ids = [
        ...new Set([
          ...Object.keys(selectedPins).filter((id) => selectedPins[id]),
          ...upcoming.current.filter((e) => e.next_visit).map((e) => e.episode_id),
        ]),
      ];
      const result = await controller.prepareEpisodes(ids.slice(0, 200));
      setMessage(
        `${result.prepared.length} episodes prepared. ${result.failed.length} could not be prepared; existing encrypted records and pending entries are retained.${ids.length > 200 ? ' Limit 200 per run; reduce pins or prepare remaining charts individually.' : ''}`,
      );
    } catch {
      setMessage(
        'Preparation could not finish. Stay connected, verify authorization and retry; existing local records are retained.',
      );
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!query.data || !views.data || auto.current === scope || all || search) return;
    auto.current = scope;
    void prepare(false, pinView?.expanded || {});
  });
  return (
    <details>
      <summary>Prepare upcoming encounters and pinned episodes</summary>
      <div className="space-y-3 p-2">
        <p>
          While connected and unlocked, existing episodes with documented encounter dates in the next 14 days and your
          pins are prepared automatically. No appointments or episodes are created. Up to 200 episodes per run.
        </p>
        <label>
          <input checked={all} type="checkbox" onChange={(e) => setAll(e.target.checked)} />
          Find other initialized episodes to pin
        </label>
        {all && (
          <label>
            Find patient{' '}
            <input value={search} className="min-h-11 border" onChange={(e) => setSearch(e.target.value)} />
          </label>
        )}
        {query.data?.data.map((c) => (
          <label key={c.episode_id} className="block">
            <input
              type="checkbox"
              checked={!!pins[c.episode_id]}
              onChange={(e) => setPins((p) => ({ ...p, [c.episode_id]: e.target.checked }))}
            />
            {c.label} · {c.next_visit ? `encounter ${c.next_visit}` : 'no encounter in the next 14 days'}
          </label>
        ))}
        {(query.data?.total || 0) > 200 && <p>Showing the first 200 matches; narrow the patient search for others.</p>}
        <Button
          variant="outline"
          disabled={busy || controller.busy || !navigator.onLine}
          onClick={() => void prepare(true)}>
          {busy ? 'Preparing encrypted records…' : 'Save pins and prepare upcoming episodes'}
        </Button>
        {message && <p role="status">{message}</p>}
        {query.isError && <p role="alert">Upcoming encounter inventory unavailable.</p>}
      </div>
    </details>
  );
}
